import {
  Bell,
  BookOpen,
  CalendarClock,
  LayoutDashboard,
  Link2,
  ListTree,
  Mail,
  MessageSquare,
  OctagonAlert,
  Route,
  Sparkles,
  UserRound,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export type AgentSectionId =
  | "dashboard"
  | "timeline"
  | "email"
  | "schedules"
  | "connections"
  | "tools"
  | "knowledge"
  | "skills"
  | "playbook"
  | "stops"
  | "voice"
  | "owner"
  | "notifications";

/** Sub-pages under Settings → Email. */
export type AgentEmailSettingsPane = "personal" | "group";

export type AgentNavItem = {
  id: AgentSectionId;
  name: string;
  icon: LucideIcon;
  /** Shown in the top-right agent menu under Settings. */
  inMenu: boolean;
  kind: "page" | "config";
  body?: string;
};

export type AgentWorkspacePath = {
  agentId: string;
  section: AgentSectionId;
  /** Set when section is email (personal or group). */
  emailPane?: AgentEmailSettingsPane;
};

/** Top-level agent pages shown above Settings in the sidebar. */
export const AGENT_PAGE_NAV_ITEMS: AgentNavItem[] = [
  { id: "dashboard", name: "Dashboard", icon: LayoutDashboard, inMenu: false, kind: "page" },
  { id: "timeline", name: "Timeline", icon: ListTree, inMenu: false, kind: "page" },
];

/** Settings children. Email is first (Personal / Group nest under it in the shell). */
export const AGENT_SETTINGS_NAV_ITEMS: AgentNavItem[] = [
  {
    id: "email",
    name: "Email",
    icon: Mail,
    inMenu: true,
    kind: "config",
    body: "Address this agent sends as.",
  },
  {
    id: "schedules",
    name: "Schedules",
    icon: CalendarClock,
    inMenu: true,
    kind: "config",
    body: "Organization schedules assigned to this agent.",
  },
  {
    id: "connections",
    name: "Connections",
    icon: Link2,
    inMenu: true,
    kind: "config",
    body: "Systems this agent may use. Org-level connections are managed under Connections. Agent overrides come later.",
  },
  {
    id: "tools",
    name: "Tools / actions",
    icon: Wrench,
    inMenu: true,
    kind: "config",
    body: "What the agent may do on each connection (update lead, send email, reply ticket). Stub for Phase 1.",
  },
  {
    id: "knowledge",
    name: "Knowledge base",
    icon: BookOpen,
    inMenu: true,
    kind: "config",
    body: "Author libraries, books, and articles under Knowledge. Assigning that knowledge to this agent comes later.",
  },
  {
    id: "skills",
    name: "Skills",
    icon: Sparkles,
    inMenu: true,
    kind: "config",
    body: "Cross-cutting abilities that are not a connected system (example: web browse).",
  },
  {
    id: "playbook",
    name: "Playbook",
    icon: Route,
    inMenu: true,
    kind: "config",
    body: "Happy path for the job, kept short for v1.",
  },
  {
    id: "stops",
    name: "Stops / escalations",
    icon: OctagonAlert,
    inMenu: true,
    kind: "config",
    body: "When work must stop for a human (discount, legal, VIP).",
  },
  {
    id: "voice",
    name: "Voice",
    icon: MessageSquare,
    inMenu: true,
    kind: "config",
    body: "Tone, signature, and persona for outbound messages.",
  },
  {
    id: "owner",
    name: "Human owner",
    icon: UserRound,
    inMenu: true,
    kind: "config",
    body: "Who Overseer notifies on escalations. Must be a user with at least Standard access.",
  },
  {
    id: "notifications",
    name: "Notifications",
    icon: Bell,
    inMenu: true,
    kind: "config",
    body: "Escalation hooks for this agent. Org-wide prefs live under Settings.",
  },
];

/** Sidebar order: Dashboard, Timeline, then Settings children. */
export const AGENT_NAV_ITEMS: AgentNavItem[] = [
  ...AGENT_PAGE_NAV_ITEMS,
  ...AGENT_SETTINGS_NAV_ITEMS,
];

export const AGENT_EMAIL_SETTINGS_PANES: {
  id: AgentEmailSettingsPane;
  name: string;
}[] = [
  { id: "personal", name: "Personal" },
  { id: "group", name: "Group" },
];

const SECTION_IDS = new Set(AGENT_NAV_ITEMS.map((item) => item.id));
const SETTINGS_SECTION_IDS = new Set(AGENT_SETTINGS_NAV_ITEMS.map((item) => item.id));

export function isAgentSectionId(value: string | undefined): value is AgentSectionId {
  return Boolean(value && SECTION_IDS.has(value as AgentSectionId));
}

export function isAgentSettingsSectionId(value: string | undefined): value is AgentSectionId {
  return Boolean(value && SETTINGS_SECTION_IDS.has(value as AgentSectionId));
}

export function isAgentEmailSettingsPane(
  value: string | undefined,
): value is AgentEmailSettingsPane {
  return value === "personal" || value === "group";
}

export function agentSectionPath(
  agentId: string,
  section: AgentSectionId = "dashboard",
  emailPane: AgentEmailSettingsPane = "personal",
): string {
  if (section === "dashboard") return `/agents/${agentId}`;
  if (section === "timeline") return `/agents/${agentId}/timeline`;
  if (section === "email") return `/agents/${agentId}/settings/email/${emailPane}`;
  return `/agents/${agentId}/settings/${section}`;
}

/**
 * True for `/agents/:agentId`, `/agents/:agentId/timeline`,
 * `/agents/:agentId/settings/:setting`, and
 * `/agents/:agentId/settings/email/{personal|group}`.
 * Bare `/agents/:id/settings/email` returns section email without emailPane
 * so callers can redirect to personal. Legacy flat config paths return null.
 */
export function parseAgentWorkspacePath(pathname: string): AgentWorkspacePath | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "agents" || parts.length < 2) return null;
  const agentId = parts[1];
  if (!agentId || agentId === "new") return null;
  if (parts.length === 2) return { agentId, section: "dashboard" };
  if (parts.length === 3 && parts[2] === "timeline") {
    return { agentId, section: "timeline" };
  }
  if (parts.length >= 4 && parts[2] === "settings") {
    if (parts[3] === "email") {
      if (parts.length === 4) return { agentId, section: "email" };
      if (parts.length === 5 && isAgentEmailSettingsPane(parts[4])) {
        return { agentId, section: "email", emailPane: parts[4] };
      }
      return null;
    }
    if (parts.length === 4 && isAgentSettingsSectionId(parts[3])) {
      return { agentId, section: parts[3] };
    }
  }
  return null;
}

/**
 * Old flat config URLs such as `/agents/:id/email` or `/agents/:id/schedules`.
 * Used to redirect into `/agents/:id/settings/...`.
 */
export function parseLegacyAgentSectionPath(
  pathname: string,
): { agentId: string; section: AgentSectionId } | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "agents" || parts.length !== 3) return null;
  const agentId = parts[1];
  const section = parts[2];
  if (!agentId || agentId === "new") return null;
  if (section === "timeline" || section === "dashboard") return null;
  if (!isAgentSettingsSectionId(section)) return null;
  return { agentId, section };
}

/** Settings links for the agent overflow menu (same order as the sidebar). */
export function agentSettingsNavItems(): AgentNavItem[] {
  return AGENT_SETTINGS_NAV_ITEMS.filter((item) => item.inMenu);
}

/** @deprecated Prefer agentSettingsNavItems(). */
export function agentConfigMenuItems(): AgentNavItem[] {
  return agentSettingsNavItems();
}
